import { SetMirrorData } from '@type/job-add';
import {
  Label, ComboBox, Input, Button,
  RecordInterface, SetFormValues,
} from '@components/common/Forms';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useBjModal } from '@hooks/common/useBjModal';
import { SearchEmployerModal } from '@components/templates/job-add/edit/modals/SearchEmployerModal';
import { findInitialEmployer, SimpleEmployerProfileList } from '@components/templates/job-add/edit/functions/jobCompanyNameFunctions';
import { SearchEmployer } from '@components/templates/job-add/edit/form/partials/SearchEmployer';
import { useHeaderInfoData } from '@components/layout/Header/MainNavbar/data/useHeaderInfoData';

/** Props */
interface JobCompanyNameProps {
  hasEmployerProfile?: boolean,
  apiProfileList?: SimpleEmployerProfileList[],
  record: RecordInterface,
  formValues: object,
  setFormValues: SetFormValues,
  initialSelectedValues?: string,
  locale: Locale,
  radioName?: string,
  labelDescriptionClass?: string,
  setMirrorData?: SetMirrorData,
  bundleIdentifier?: string,
}

/**
 * @description Job Employer name component. To be used in a form. The text input field is omitted from submit.
 * If we don't have an employer profile, we display the search directly.
 * - `hasEmployerProfile` - boolean, if the employer has a profile or not
 * - `apiProfileList` - The employers profile list from API.
 * - `record` - record function from useBjForm
 * - `formValues` - form values object from useBjForm
 * - `setFormValues` - setFormValues function from useBjForm
 * - `initialSelectedValues` - initial selected values for combobox
 * - `locale` - locale
 * - `radioName` - name of the radio input; default is 'companyName'
 * - `labelDescriptionClass` - additional classes for the label description
 * - `setMirrorData` - function to update the mirror form state
 */
export const JobCompanyName = (props: JobCompanyNameProps) => {
  // Destructure props
  const {
    hasEmployerProfile,
    apiProfileList,
    record,
    formValues,
    setFormValues,
    initialSelectedValues,
    locale,
    radioName = 'companyName',
    labelDescriptionClass = '',
    setMirrorData,
    bundleIdentifier,
  } = props;

  // Translation
  const { t } = useTranslation();

  // Premium info from header-info and local job bundle logic
  const { headerInfoData } = useHeaderInfoData(locale);
  const hasSubscription = !!headerInfoData?.hasSubscription;
  const availability = hasSubscription || bundleIdentifier === 'pro-fix';

  // State for mutated employer profile list
  const [employerList, setEmployerList] = useState<SimpleEmployerProfileList[] | undefined>(apiProfileList);

  // Modal hook
  // **************************
  const {
    isOpen,
    toggleModal,
  } = useBjModal();

  // Track availability to clear confidential selection when it becomes unavailable
  const prevAvailability = useRef(availability);

  useEffect(() => {
    if (prevAvailability.current && !availability) {
      const currentValue = formValues?.[radioName as keyof typeof formValues] as string[] | string;
      const isConfidentialSelected = Array.isArray(currentValue)
        ? currentValue.includes('confidential')
        : currentValue === 'confidential';

      if (isConfidentialSelected) {
        const firstEmployer = employerList?.find(
          (emp) => emp.id !== 'confidential'
            && emp.id !== 'searchEmployerButton'
            && !emp.name?.includes('searchEmployerButton'),
        );
        const resetValue = firstEmployer?.radioValue || initialSelectedValues || '';
        setFormValues({ [radioName]: resetValue });
      }
    }

    prevAvailability.current = availability;
  }, [availability, employerList, formValues, initialSelectedValues, radioName, setFormValues]);

  // Render
  // **************************
  return (
    <>
      {/* Has employer profile */}
      {hasEmployerProfile && (
        <>
          {/* Company name field */}
          <Label
            as="div"
            description={t('job.add.label.employer')}
            descriptionClass={labelDescriptionClass}
          >
            <ComboBox
              key="employer-combobox"
              className="max-w-full text-ellipsis pr-18"
              options={{
                staticList: employerList,
                valueFromKey: 'name',
                translateFn: t,
                unmount: false,
                keepOpen: true,
                behaveAs: 'select',
                isItemDisabled: (item) => {
                  // we don't want to allow selecting the search employer button
                  const isSearchButton = item['name' as keyof typeof item] === 'searchEmployerButton';
                  // Confidential is disabled if not available (determined by backend)
                  const isConfidentialDisabled = item['id' as keyof typeof item] === 'confidential'
                    && !availability;
                  return isSearchButton || isConfidentialDisabled;
                },
                selectOptions: {
                  setFormValues,
                  checkboxValueFromKey: 'radioValue',
                  checkboxName: radioName,
                  initialSelectedValues: findInitialEmployer(employerList, initialSelectedValues),
                  updatedSelectedValues: formValues?.[radioName as keyof typeof formValues],
                },
              }}
              onClear={() => {
                setFormValues({ [radioName]: '' });
              }}
              renderList={(
                item,
                index,
                listKey,
                getItemProps,
                listStyling,
                _highlightedIndex,
                isDisabled,
              ) => {
                const currentItem = item as SimpleEmployerProfileList;
                const {
                  id,
                  name,
                  radioValue,
                  fromSearch,
                  isSearchButton,
                  disabledReason,
                } = currentItem;

                // Render list item
                return (
                  <li key={listKey} {...getItemProps({ item, index })} className={`px-3${isDisabled && name !== 'searchEmployerButton' ? ' cursor-not-allowed text-ink-light' : ''}`}>
                    {/* Selectable radio item */}
                    {!isSearchButton && !fromSearch && (
                    <span className={`${listStyling} items-center text-sm`}>
                      <Input
                        type="radio"
                        disabled={isDisabled}
                        {...isDisabled ? { className: 'pointer-events-none !border-input/90' } : {}}
                        {...record(radioName, {
                          id: `${radioName}-${id}`,
                          defaultValue: radioValue,
                          defaultChecked: radioValue === findInitialEmployer(employerList, initialSelectedValues),
                        })}
                      />
                      <span className="pl-2.5">
                        {name}
                        {isDisabled && disabledReason && (` - ${t(disabledReason)}`)}
                      </span>
                    </span>
                    )}

                    {/* Name added from modal search. The radio should be hidden */}
                    {!isSearchButton && fromSearch && (
                    <span className="hidden">
                      <Input
                        type="radio"
                        {...record(radioName, {
                          id: `${radioName}-${id}`,
                          defaultValue: radioValue,
                        })}
                      />
                      {name}
                    </span>
                    )}

                    {/* Search employer button. No radio. */}
                    {isSearchButton && (
                    <span className={`${listStyling} !px-0 !pt-3`}>
                      <Button
                        size="sm"
                        styling="soft"
                        color="primary-light"
                        className="w-full"
                        onClick={toggleModal}
                      >
                        {t('job.add.label.employer.other')}
                      </Button>
                    </span>
                    )}
                  </li>
                );
              }}
              {...record('companyNameSelector', {
                removeFromSubmit: true,
              })}
            />
          </Label>

          {/* Search Employer modal */}
          <SearchEmployerModal
            locale={locale}
            isOpen={isOpen}
            toggleModal={toggleModal}
            labelDescriptionClass={labelDescriptionClass}
            setEmployerList={setEmployerList}
            setFormValues={setFormValues}
            inputNameForSet={radioName}
            setMirrorData={setMirrorData}
          />
        </>
      )}

      {/* No employer profile */}
      {!hasEmployerProfile && (
        <SearchEmployer
          record={record}
          labelDescriptionClass={labelDescriptionClass}
          fieldName={radioName}
        />
      )}
    </>
  );
};
